import * as XLSX from "xlsx";
import { PRODUCT_STOCK_COUNTRIES } from "../constants/ProductConstant";
import { formatPhoneNumber } from "../functions/formatters";
import { collection, getDocs } from "firebase/firestore";
import { firestore } from "../configs/FirebaseConfig";
import { FIRESTORE_LEADS_TABLE, FIRESTORE_REGIONS_TABLE } from "../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../constants/CompanyConstants";

/**
 * Generate an Excel template for lead imports with dropdown validation
 * @returns {Blob} Excel file blob ready for download
 */
export const generateLeadsExcelTemplate = () => {
  const wb = XLSX.utils.book_new();

  // Create headers
  const headers = [
    "Company Name*",
    "Contact First Name",
    "Contact Last Name",
    "Email",
    "Phone",
    "Street*",
    "House Number*",
    "House Number Addition",
    "Zip Code*",
    "City*",
    "Country*",
    "Origin",
    "Website",
    "Company Type",
    "Delivery Instructions",
    "Internal Notes",
  ];

  // Create worksheet with headers
  const ws = XLSX.utils.aoa_to_sheet([headers]);

  // Set column widths
  ws["!cols"] = [
    { wch: 20 }, // Company Name
    { wch: 18 }, // Contact First Name
    { wch: 18 }, // Contact Last Name
    { wch: 25 }, // Email
    { wch: 15 }, // Phone
    { wch: 20 }, // Street
    { wch: 12 }, // House Number
    { wch: 12 }, // Addition
    { wch: 12 }, // Zip Code
    { wch: 15 }, // City
    { wch: 10 }, // Country
    { wch: 15 }, // Origin
    { wch: 25 }, // Website
    { wch: 15 }, // Company Type
    { wch: 30 }, // Delivery Instructions
    { wch: 30 }, // Internal Notes
  ];

  // Add data validation for Country column (K = column 11, index 10)
  const countryOptions = PRODUCT_STOCK_COUNTRIES.map((c) => c.code).join(",");

  // Create validation sheet with options
  const validationSheet = XLSX.utils.aoa_to_sheet([["Countries"], ...PRODUCT_STOCK_COUNTRIES.map((c) => [c.code])]);

  // Add validation sheet to workbook
  XLSX.utils.book_append_sheet(wb, validationSheet, "Validation");

  // Add data validation to main sheet
  // Note: XLSX library has limited support for data validation,
  // so we'll add instructions in a separate sheet
  const instructionsData = [
    ["Instructions for filling the Lead Import Template"],
    [""],
    ["Required fields are marked with * - these must be filled"],
    [""],
    ["Country Options:", countryOptions],
    [""],
    ["Phone Number:", "Must include country code (e.g., +31612345678)"],
    ["Email:", "Must be a valid email address"],
    ["Origin:", "Free text field - enter any value"],
    [""],
    ["Tips:"],
    ["- Make sure there are no duplicate emails or phone numbers in your file"],
    ["- Remove any test/example rows before uploading"],
    ["- Save the file as .xlsx format"],
  ];

  const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionsData);
  instructionsSheet["!cols"] = [{ wch: 50 }, { wch: 50 }];
  XLSX.utils.book_append_sheet(wb, instructionsSheet, "Instructions");

  // Add main sheet
  XLSX.utils.book_append_sheet(wb, ws, "Leads");

  // Generate buffer
  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
};

/**
 * Validate email format
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number format and length
 */
const isValidPhone = (phone) => {
  try {
    const formatted = formatPhoneNumber(phone);
    // Check if formatted phone has at least 10 digits (excluding +)
    const digitsOnly = formatted.replace(/\D/g, "");
    return digitsOnly.length >= 10;
  } catch (error) {
    return false;
  }
};

/**
 * Validate a single lead row
 * Returns { errors: [], warnings: [] }
 * - errors: blocking issues that prevent import
 * - warnings: non-blocking issues (like duplicates) that allow import with user awareness
 */
const validateLeadRow = (row, rowIndex, existingEmails, existingPhones, seenEmails, seenPhones, t) => {
  const errors = [];
  const warnings = [];

  // Required field validation
  if (!row.companyName?.trim()) {
    errors.push(t("form.enter_company_name"));
  }
  if (!row.phone?.trim()) {
    warnings.push(t("warning_no_phone_number"));
  }
  if (!row.street?.trim()) {
    errors.push(t("form.enter_street"));
  }
  if (!row.houseNumber?.trim()) {
    errors.push(t("form.enter_house_number"));
  }
  if (!row.zipCode?.trim()) {
    errors.push(t("form.enter_zip_code"));
  }
  if (!row.city?.trim()) {
    errors.push(t("form.enter_city"));
  }
  if (!row.country?.trim()) {
    errors.push(t("form.enter_country"));
  }

  // Email validation
  if (row.email?.trim()) {
    const email = row.email.trim().toLowerCase();

    if (!isValidEmail(email)) {
      errors.push(t("form.enter_valid_email"));
    }

    // Check duplicate in database - WARNING only
    if (existingEmails.has(email)) {
      warnings.push(t("warning_lead_email_exists"));
    }

    // Check duplicate within Excel file - WARNING only
    if (seenEmails.has(email)) {
      warnings.push(t("warning_duplicate_email_in_excel"));
    } else {
      seenEmails.add(email);
    }
  }

  // Phone validation
  if (row.phone?.trim()) {
    const phone = row.phone.trim();

    // Check if phone starts with 0 (missing country code)
    if (phone.startsWith("0")) {
      warnings.push(t("warning_phone_missing_country_code"));
    }

    // Check if phone has 10 or fewer digits (missing country code - Excel may have removed leading 0)
    const digitsOnly = phone.replace(/\D/g, "");
    if (digitsOnly.length <= 10) {
      warnings.push(t("warning_phone_too_short"));
    }

    if (!isValidPhone(phone)) {
      errors.push(t("form.enter_valid_phone"));
    }

    const formattedPhone = formatPhoneNumber(phone);

    // Check duplicate in database - WARNING only
    if (existingPhones.has(formattedPhone)) {
      warnings.push(t("warning_phone_already_exists"));
    }

    // Check duplicate within Excel file - WARNING only
    if (seenPhones.has(formattedPhone)) {
      warnings.push(t("warning_duplicate_phone_in_excel"));
    } else {
      seenPhones.add(formattedPhone);
    }
  }

  // Country validation
  if (row.country?.trim()) {
    const validCountries = PRODUCT_STOCK_COUNTRIES.map((c) => c.code);
    if (!validCountries.includes(row.country.trim().toUpperCase())) {
      errors.push(t("invalid_country_code"));
    }
  }

  return { errors, warnings };
};

/**
 * Parse and validate Excel file with leads
 * @param {File} file - Excel file
 * @param {Function} t - Translation function
 * @returns {Promise<{validLeads: Array, invalidLeads: Array}>}
 */
export const parseExcelLeads = async (file, t) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        // Get the first sheet (assuming leads are in the first sheet)
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        if (jsonData.length === 0) {
          reject(new Error(t("no_leads_found_in_excel")));
          return;
        }

        // Fetch existing emails and phones from database
        const leadsSnapshot = await getDocs(
          collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE),
        );
        const existingEmails = new Set();
        const existingPhones = new Set();

        leadsSnapshot.forEach((doc) => {
          const lead = doc.data();
          if (lead.email) {
            existingEmails.add(lead.email.toLowerCase());
          }
          if (lead.phone) {
            existingPhones.add(lead.phone);
          }
        });

        // Track duplicates within the Excel file
        const seenEmails = new Set();
        const seenPhones = new Set();

        const validLeads = [];
        const invalidLeads = [];

        // Process each row
        jsonData.forEach((row, index) => {
          // Map Excel columns to lead object
          const rawZipCode = row["Zip Code*"]?.toString().trim() || "";
          const country = row["Country*"]?.toString().trim().toUpperCase() || "";

          const lead = {
            companyName: row["Company Name*"]?.toString().trim() || "",
            contactFirstName: row["Contact First Name"]?.toString().trim() || "",
            contactLastName: row["Contact Last Name"]?.toString().trim() || "",
            email: (row["Email*"] || row["Email"])?.toString().trim().toLowerCase() || "",
            phone: (row["Phone*"] || row["Phone"])?.toString().trim() || "",
            street: row["Street*"]?.toString().trim() || "",
            houseNumber: row["House Number*"]?.toString().trim() || "",
            houseNumberAddition: row["House Number Addition"]?.toString().trim() || "",
            zipCode: rawZipCode,
            city: row["City*"]?.toString().trim() || "",
            country: country,
            origin: row["Origin"]?.toString().trim() || "",
            website: row["Website"]?.toString().trim() || "",
            companyType: row["Company Type"]?.toString().trim() || "",
            deliveryInstructions: row["Delivery Instructions"]?.toString().trim() || "",
            notes: row["Internal Notes"]?.toString().trim() || "",
          };

          // Validate the lead
          const { errors, warnings } = validateLeadRow(
            lead,
            index + 2, // +2 because Excel is 1-indexed and we have a header row
            existingEmails,
            existingPhones,
            seenEmails,
            seenPhones,
            t,
          );

          if (errors.length > 0) {
            // Only blocking errors prevent import
            invalidLeads.push({
              row: index + 2,
              lead,
              errors,
            });
          } else {
            // Format phone number for storage (only if phone exists)
            if (lead.phone) {
              lead.phone = formatPhoneNumber(lead.phone);
            }

            // Build address
            lead.address =
              lead.street +
              " " +
              lead.houseNumber +
              (lead.houseNumberAddition ? " " + lead.houseNumberAddition : "") +
              ", " +
              lead.city +
              ", " +
              lead.zipCode;

            // Add warnings to lead object if any exist
            if (warnings.length > 0) {
              lead.warnings = warnings;
            }

            validLeads.push(lead);
          }
        });

        resolve({ validLeads, invalidLeads });
      } catch (error) {
        console.error("Error parsing Excel:", error);
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error(t("error_reading_file")));
    };

    reader.readAsArrayBuffer(file);
  });
};
